import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { Alert, Button, Card, CardBody, Input, Label, Modal, Skeleton } from '@/components/ui';
import { EmptyState, PageHeader, Pagination } from '@/components/domain';
import { IntegrationSettingRow } from '@/components/IntegrationSettingRow';
import type { IntegrationSettingItem } from '@/types';

/** Siden viser 50 raekker ad gangen. Filtrering og sidedeling sker i browseren: svaret er
 *  hele tabellen, og den er lille nok til at et rundt kald pr. side ville koste mere end det
 *  gav. */
const PAGE_SIZE = 50;

export function AdminIntegrationsPage() {
  /*
   * 'Se integrationer' uden 'Ændr': navnene og om de er sat. The values take 'Se
   * integrationsnøgler' - which 'Ændr integrationer' requires.
   */
  const { can } = useAuth();
  const canEdit = can('integrations.manage');
  const canReveal = can('integrations.reveal');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  /**
   * Konfigurationen opdateres ikke paa interval: en refetch midt i en indtastning ville
   * skifte felterne under fingrene. Vaerdierne er ikke med i svaret - de hentes enkeltvis,
   * naar øjet klikkes.
   */
  const { data: config, isLoading } = useQuery({
    queryKey: ['admin', 'integration-config'],
    queryFn: () =>
      api.get<{ settings_loaded: boolean; data: IntegrationSettingItem[] }>('/integrations/config'),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const term = search.trim().toLowerCase();
  const matches = (config?.data ?? []).filter((setting) =>
    term.length === 0 ? true : setting.name.toLowerCase().includes(term),
  );
  const page = matches.slice(offset, offset + PAGE_SIZE);

  return (
    <>
      <PageHeader
        title="Integrationer"
        description={
          canEdit
            ? 'Nøgler og værdier fra codepass_integrations. En gemt værdi gælder med det samme.'
            : canReveal
              ? 'Nøglerne i codepass_integrations. Din rolle kan se værdierne, men ikke ændre dem.'
              : 'Nøglerne i codepass_integrations, og om de er sat. Din rolle kan ikke se eller ændre værdierne.'
        }
      />

      {config && !config.settings_loaded ? (
        <Alert tone="warning" className="mb-4">
          Konfigurationen kunne ikke læses fra databasen, da API&apos;et startede. Rækkerne herunder
          er tabellens indhold, men integrationerne kender dem ikke, før en værdi gemmes eller
          API&apos;et genstartes.
        </Alert>
      ) : null}

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line p-3">
          <Input
            className="max-w-sm"
            placeholder="Søg på nøglens navn"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              // Uden nulstillingen kunne en soegning lande paa side 3 af ét resultat.
              setOffset(0);
            }}
          />
          {canEdit ? (
            <Button
              size="icon"
              variant="secondary"
              onClick={() => setAddOpen(true)}
              aria-label="Tilføj nøgle"
            >
              <Plus className="h-4 w-4" />
            </Button>
          ) : null}
        </div>

        {isLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : matches.length === 0 ? (
          <EmptyState
            title={term.length > 0 ? 'Ingen nøgler matcher søgningen' : 'Ingen nøgler endnu'}
            description={
              term.length > 0 || !canEdit
                ? undefined
                : 'Tilføj den første nøgle med plusset øverst til højre.'
            }
          />
        ) : (
          <>
            <CardBody>
              {page.map((setting) => (
                <IntegrationSettingRow
                  key={setting.name}
                  setting={setting}
                  readOnly={!canEdit}
                  canReveal={canReveal}
                />
              ))}
            </CardBody>
            <Pagination
              total={matches.length}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>

      <AddSettingModal open={addOpen} onClose={() => setAddOpen(false)} />
    </>
  );
}

/**
 * Opret en ny raekke i codepass_integrations.
 *
 * Findes, fordi listen kun viser tabellens raekker: uden den kunne en admin rette det der
 * staar, men ikke tilfoeje noget - og saa ville en ny noegle stadig kraeve SQL, som er praecis
 * det siden skal goere unoedvendigt.
 */
/**
 * Navnet skrives som en miljoevariabel: STORE BOGSTAVER og understreg.
 *
 * Rettes mens der tastes frem for ved gemning. Serveren afviser alligevel mellemrum, og en
 * fejlbesked efter et klik er en daarligere forklaring end et felt der bare skriver det
 * rigtige.
 */
function normaliseSettingName(input: string): string {
  return input.toUpperCase().replaceAll(/[\s-]+/g, '_');
}

function AddSettingModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const close = (): void => {
    setName('');
    setValue('');
    setError(null);
    onClose();
  };

  const create = useMutation({
    mutationFn: () =>
      api.put<{ name: string }>(`/integrations/config/${encodeURIComponent(name.trim())}`, {
        value,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'integration-config'] });
      close();
    },
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke oprette nøglen.'),
  });

  return (
    <Modal
      open={open}
      onClose={close}
      title="Tilføj nøgle"
      description="Navnet er integration_name, og værdien er integration_value."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Annullér
          </Button>
          <Button
            disabled={name.trim().length === 0}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Opret
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="new-setting-name">Navn</Label>
          <Input
            id="new-setting-name"
            className="font-mono text-xs"
            placeholder="DYNAMICS_CLIENT_SECRET"
            value={name}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setName(normaliseSettingName(event.target.value))}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="new-setting-value">Værdi</Label>
          <Input
            id="new-setting-value"
            className="font-mono text-xs"
            value={value}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setValue(event.target.value)}
          />
        </div>

        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </div>
    </Modal>
  );
}
